/**
 * Единственная точка соприкосновения с Telegram.
 *
 * Внутри Telegram данные запуска берутся через @tma.js/sdk-react, а вызовы
 * методов идут в `window.Telegram.WebApp`: это стабильный и полный интерфейс,
 * который отдаёт сам клиент. Вне Telegram (обычный браузер при разработке) все
 * вызовы превращаются в no-op, а приложение продолжает работать.
 */
import { retrieveLaunchParams, retrieveRawInitData } from '@tma.js/sdk-react';
import type { TelegramThemeParams, TelegramWebApp } from '../types/telegram';
import { parseInitDataUser } from './init-user';
import type { TelegramUser } from './init-user';
import { t } from '../i18n';

/** Сколько ждём ответа клиента на запрос разрешения писать в чат. */
const WRITE_ACCESS_TIMEOUT_MS = 5000;

/** Фон приложения, тот же, что --bg в styles.css: в него красится и обвязка клиента. */
const CHROME_COLOR = '#0a0a0a';

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotification = 'error' | 'success' | 'warning';

function webApp(): TelegramWebApp | undefined {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp;
}

function readRawInitData(): string | undefined {
  try {
    const raw = retrieveRawInitData();
    if (raw) return raw;
  } catch {
    // Вне Telegram SDK бросает — это ожидаемо.
  }
  const data = webApp()?.initData;
  return data && data.length > 0 ? data : undefined;
}

function readStartParam(): string | undefined {
  try {
    const params = retrieveLaunchParams();
    const value = (params as { tgWebAppStartParam?: string }).tgWebAppStartParam;
    if (value) return value;
  } catch {
    // см. выше
  }
  const fromWebApp = webApp()?.initDataUnsafe?.start_param;
  if (fromWebApp) return fromWebApp;
  if (typeof window !== 'undefined') {
    // Удобно для разработки: ?startapp=project_<id> в обычном браузере.
    const url = new URL(window.location.href);
    return url.searchParams.get('startapp') ?? url.searchParams.get('tgWebAppStartParam') ?? undefined;
  }
  return undefined;
}

const app = webApp();

export const isTelegram: boolean = app !== undefined && readRawInitData() !== undefined;
export const hasWebAppApi: boolean = app !== undefined;
export const initDataRaw: string | undefined = readRawInitData();
export const startParam: string | undefined = readStartParam();
/** Кто открыл приложение: имя и фото для профиля. Вне Telegram — null. */
export const user: TelegramUser | null = initDataRaw ? parseInitDataUser(initDataRaw) : null;
export const platform: string = app?.platform ?? 'unknown';
export const isIos: boolean =
  platform === 'ios' ||
  (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent));

export function themeParams(): TelegramThemeParams {
  return webApp()?.themeParams ?? {};
}

export function colorScheme(): 'light' | 'dark' {
  return webApp()?.colorScheme ?? 'light';
}

/** Прокидывает тему в CSS-переменные. В Telegram клиент делает это сам, дублирование безвредно. */
function applyTheme(): void {
  if (typeof document === 'undefined') return;
  const params = themeParams();
  const root = document.documentElement;
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string') {
      root.style.setProperty(`--tg-theme-${key.replace(/_/g, '-')}`, value);
    }
  }
  root.dataset['scheme'] = colorScheme();
}

/**
 * Тема приложения фиксированная тёмная, поэтому шапку, фон и нижнюю панель
 * самого клиента красим в наш цвет: иначе в светлой теме Telegram вокруг
 * тёмного экрана остаются белые полосы.
 */
function paintChrome(instance: TelegramWebApp): void {
  try {
    instance.setHeaderColor?.(CHROME_COLOR);
    instance.setBackgroundColor?.(CHROME_COLOR);
    instance.setBottomBarColor?.(CHROME_COLOR);
  } catch {
    // Старые клиенты не знают этих методов.
  }
}

let themeListeners: Array<() => void> = [];

export function onThemeChange(listener: () => void): () => void {
  themeListeners.push(listener);
  return () => {
    themeListeners = themeListeners.filter((item) => item !== listener);
  };
}

/** Разворачивает окно, включает защиту от случайного закрытия свайпом. */
export function init(): void {
  const instance = webApp();
  if (!instance) return;
  try {
    instance.ready();
    instance.expand();
    instance.disableVerticalSwipes?.();
    applyTheme();
    paintChrome(instance);
    instance.onEvent('themeChanged', () => {
      applyTheme();
      paintChrome(instance);
      for (const listener of themeListeners) listener();
    });
  } catch {
    // Старые клиенты могут не знать часть методов.
  }
}

export function expand(): void {
  webApp()?.expand();
}

export function enableClosingConfirmation(): void {
  webApp()?.enableClosingConfirmation();
}

export function disableClosingConfirmation(): void {
  webApp()?.disableClosingConfirmation();
}

export function disableVerticalSwipes(): void {
  webApp()?.disableVerticalSwipes?.();
}

export function enableVerticalSwipes(): void {
  webApp()?.enableVerticalSwipes?.();
}

export function close(): void {
  webApp()?.close();
}

let backButtonHandler: (() => void) | null = null;

export const backButton = {
  set(onClick: (() => void) | null): void {
    const button = webApp()?.BackButton;
    if (!button) return;
    if (backButtonHandler) {
      button.offClick(backButtonHandler);
      backButtonHandler = null;
    }
    if (!onClick) {
      button.hide();
      return;
    }
    backButtonHandler = onClick;
    button.onClick(backButtonHandler);
    button.show();
  },
};

export const hapticFeedback = {
  impact(style: HapticStyle = 'light'): void {
    try {
      webApp()?.HapticFeedback.impactOccurred(style);
    } catch {
      /* нет поддержки — не беда */
    }
  },
  notification(type: HapticNotification): void {
    try {
      webApp()?.HapticFeedback.notificationOccurred(type);
    } catch {
      /* см. выше */
    }
  },
  selection(): void {
    try {
      webApp()?.HapticFeedback.selectionChanged();
    } catch {
      /* см. выше */
    }
  },
};

/**
 * `downloadFile` доступен с Bot API 8.0. Ссылка должна быть HTTPS и отдавать
 * `Content-Disposition: attachment`. Вне Telegram открываем ссылку обычным
 * способом, чтобы кнопка работала и в браузере.
 */
export function downloadFile(url: string, name: string): Promise<boolean> {
  return new Promise((resolve) => {
    const instance = webApp();
    if (isTelegram && instance?.downloadFile) {
      try {
        instance.downloadFile({ url, file_name: name }, (accepted) => resolve(accepted));
        return;
      } catch {
        // падаем в запасной путь ниже
      }
    }
    if (typeof document !== 'undefined') {
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.rel = 'noopener';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      resolve(true);
      return;
    }
    resolve(false);
  });
}

/** Публикация в историю, подпись не длиннее 200 знаков без Premium. */
export function shareToStory(url: string, text: string): boolean {
  const instance = webApp();
  if (!instance?.shareToStory) return false;
  try {
    instance.shareToStory(url, { text: text.slice(0, 200) });
    return true;
  } catch {
    return false;
  }
}

/**
 * Разрешение боту писать пользователю — спрашиваем перед первой генерацией.
 * Вне Telegram ответа не будет никогда, поэтому есть тайм-аут: генерация не
 * должна зависать из-за необязательного запроса.
 */
export function requestWriteAccess(): Promise<boolean> {
  return new Promise((resolve) => {
    const instance = webApp();
    if (!isTelegram || !instance?.requestWriteAccess) {
      resolve(false);
      return;
    }
    let settled = false;
    const finish = (granted: boolean): void => {
      if (settled) return;
      settled = true;
      resolve(granted);
    };
    const timer = window.setTimeout(() => finish(false), WRITE_ACCESS_TIMEOUT_MS);
    try {
      instance.requestWriteAccess((granted) => {
        window.clearTimeout(timer);
        finish(granted);
      });
    } catch {
      window.clearTimeout(timer);
      finish(false);
    }
  });
}

/**
 * Открывает ссылку: t.me внутри Telegram, остальные во внешнем браузере.
 * Вне Telegram — новая вкладка, чтобы кнопки работали и при разработке.
 */
export function openLink(url: string): void {
  const instance = webApp();
  const insideTelegram = /^https:\/\/t\.me\//i.test(url);
  if (isTelegram && instance) {
    try {
      if (insideTelegram && instance.openTelegramLink) {
        instance.openTelegramLink(url);
        return;
      }
      if (instance.openLink) {
        instance.openLink(url);
        return;
      }
    } catch {
      // ниже запасной путь
    }
  }
  window.open(url, '_blank', 'noopener');
}

/**
 * Поддержка живёт в том же боте, из которого открыта Mini App. Попытка открыть
 * текущий чат через openTelegramLink в некоторых клиентах Telegram ничего не
 * делает, поэтому внутри Telegram просто закрываем Mini App и возвращаем чат.
 * В обычном браузере сохраняем рабочую ссылку как запасной путь.
 */
export function openSupportChat(fallbackUrl: string): void {
  if (isTelegram) {
    close();
    return;
  }
  openLink(fallbackUrl);
}

export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const instance = webApp();
    if (isTelegram && instance?.showConfirm) {
      try {
        instance.showConfirm(message, (ok) => resolve(ok));
        return;
      } catch {
        // ниже запасной путь
      }
    }
    resolve(typeof window !== 'undefined' ? window.confirm(message) : false);
  });
}

/** The callback is UI feedback only; the server credits successful_payment. */
export function openInvoice(url: string): Promise<'paid' | 'cancelled' | 'failed' | 'pending'> {
  const app = webApp();
  if (!app?.openInvoice) return Promise.reject(new Error(t('lib.telegram.stars_unsupported')));
  return new Promise(resolve => app.openInvoice!(url, resolve));
}

/** Telegram can resize its WebView independently of the browser visual viewport. */
export function viewportHeight(): number | undefined {
  const height = isTelegram ? webApp()?.viewportHeight : undefined;
  return height && height > 0 ? height : undefined;
}

export function onViewportChange(listener: () => void): () => void {
  const instance = webApp();
  instance?.onEvent('viewportChanged', listener);
  return () => instance?.offEvent('viewportChanged', listener);
}
